import { generateNcrNumber } from 'helpers/generators';
import { toLabelledEntity, toUnsafeLabelledEntity } from 'helpers/misc';
import { CompanyModel } from 'shared/domain/company/types/model';
import { ContractModel } from 'shared/domain/contract/types/model';
import { IssueModel } from 'shared/domain/issue/issueModel';
import { LevelModel } from 'shared/domain/level/types/model';
import { SiteModel } from 'shared/domain/site/types/model';
import { userWithPersonalDataToOptionalLabelled } from 'shared/domain/user/mapping/toView';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { FieldSkeleton } from 'shared/types/form';
import { ProcessType } from 'shared/domain/process/types/model';
import { stringToViewDate } from 'shared/utils/date/stringToViewDate';
import { toViewDate } from 'shared/utils/date/toViewDate';
import { LegacyIssueForm } from 'views/issue/issueView/UnifiedIssueViewContainer/types';
import { IssueOnSingleView } from '../withIssue/IssueOnSingleView';
import { modelToViewBase } from 'shared/domain/toBaseView';
import { getReferenceId } from 'shared/utils/id/referenceId';

export function toIssueOnTableView(
  issueModel: IssueModel,
  currentUserId: string,
  sites: SiteModel[],
  levels: LevelModel[],
  processes: ProcessType[],
  issueForms: HashMap<LegacyIssueForm>,
  subcontractors: HashMap<CompanyModel>,
  contractNumbers: HashMap<ContractModel>,
  timezone: string,
  organizationId: string
): IssueOnSingleView {
  const processId = getReferenceId(issueModel.process);
  const siteId = getReferenceId(issueModel.primaryData.site);
  const levelId = getReferenceId(issueModel.primaryData.level);

  const issueProcess = processes.find(
    ({ _id }) => _id === processId
  );
  if (!issueProcess) {
    throw new Error(
      `Cannot match process to issueModel id=${issueModel._id}, process=${JSON.stringify(issueModel.process)}`
    );
  }
  const issueSite = sites.find(
    ({ _id }) => _id === siteId
  );
  if (!issueSite) {
    throw new Error(
      `Cannot match site to issueModel id=${issueModel._id}, site=${JSON.stringify(issueModel.primaryData.site)}`
    );
  }
  const issueLevel = levels.find(
    ({ _id }) => _id === levelId
  );
  if (!issueLevel) {
    throw new Error(
      `Cannot match level to issueModel id=${issueModel._id}, level=${JSON.stringify(issueModel.primaryData.level)}`
    );
  }
  // @ts-ignore cannt use undefined as index
  const issueForm: LegacyIssueForm = issueForms[issueProcess?._id] || {
    primaryFields: [],
    extendedFields: [],
  };

  const ncrNumber = generateNcrNumber(
    issueModel.createdAt,
    issueProcess?.code ?? '',
    issueSite?.code ?? '',
    issueModel._id.slice(-4)
  );
  const onViewBase = modelToViewBase(issueModel, organizationId);
  return {
    ...onViewBase,
    extendedData: {
      effect: findValue(
        findField(issueForm, 'extendedFields', 'effect'),
        issueModel.extendedData.effect
      ),
      environmentalAspect: findValue(
        findField(issueForm, 'extendedFields', 'environmentalAspect'),
        issueModel.extendedData.environmentalAspect
      ),
      estimatedCost: issueModel.extendedData.estimatedCost
        ? issueModel.extendedData.estimatedCost?.map((costObject) => {
            const found = subcontractors[costObject.coveredBy!];
            return {
              cost: costObject.cost,
              coveredBy: found
                ? { _id: found._id, label: found.shortLabel }
                : undefined,
            };
          })
        : undefined,
      expectedFine: issueModel.extendedData.expectedFine,
      finalCompletionDate: toViewDate(
        timezone,
        issueModel.extendedData.finalCompletionDate
      ),
      finalCost: issueModel.extendedData.finalCost
        ? issueModel.extendedData.finalCost?.map((costObject) => {
            const found = subcontractors[costObject.coveredBy!];
            return {
              outstanding: costObject.outstanding,
              settled: costObject.settled,
              coveredBy: found
                ? { _id: found._id, label: found.shortLabel }
                : undefined,
            };
          })
        : undefined,
      hazardCategory: findValues(
        findField(issueForm, 'extendedFields', 'hazardCategory'),
        issueModel.extendedData.hazardCategory
      ),
      impact: findValue(
        findField(issueForm, 'extendedFields', 'impact'),
        issueModel.extendedData.impact
      ),
      imposedFine: issueModel.extendedData.imposedFine,
      rootCauses: findValues(
        findField(issueForm, 'extendedFields', 'rootCauses'),
        issueModel.extendedData.rootCauses
      ),
      solutionProposal: issueModel.extendedData.solutionProposal,
      targetCompletionDate: toViewDate(
        timezone,
        issueModel.extendedData.targetCompletionDate
      ),
      workTypes: findValues(
        findField(issueForm, 'extendedFields', 'workTypes'),
        issueModel.extendedData.workTypes
      ),
      circumstances: findValue(
        findField(issueForm, 'extendedFields', 'circumstances'),
        issueModel.extendedData.circumstances
      ),
      issueVerification: findValue(
        findField(issueForm, 'extendedFields', 'issueVerification'),
        issueModel.extendedData.issueVerification
      ),
      delayReason: findValue(
        findField(issueForm, 'extendedFields', 'delayReason'),
        issueModel.extendedData.delayReason
      ),
      rejectionReason: findValue(
        findField(issueForm, 'extendedFields', 'rejectionReason'),
        issueModel.extendedData.rejectionReason
      ),
      contaminatedSoilScale: issueModel.extendedData.contaminatedSoilScale,
      costCode: issueModel.extendedData.costCode,
      daysOfInabilityToWork: issueModel.extendedData.daysOfInabilityToWork,
      decisionToImposeFine: findValue(
        findField(issueForm, 'extendedFields', 'decisionToImposeFine'),
        issueModel.extendedData.decisionToImposeFine
      ),
      personUnableToWork: issueModel.extendedData.personUnableToWork,
      proposedCorrectiveAction: findValue(
        findField(issueForm, 'extendedFields', 'proposedCorrectiveAction'),
        issueModel.extendedData.proposedCorrectiveAction
      ),
      soilLeakageScale: issueModel.extendedData.soilLeakageScale,
      solutionMethod: issueModel.extendedData.solutionMethod,
      spilledSubstance: findValue(
        findField(issueForm, 'extendedFields', 'spilledSubstance'),
        issueModel.extendedData.spilledSubstance
      ),
      subcontractorRepresentative:
        issueModel.extendedData.subcontractorRepresentative,
      waterLeakageScale: issueModel.extendedData.waterLeakageScale,
      targetStartDate: toViewDate(
        timezone,
        issueModel.extendedData.targetStartDate
      ),
      finalStartDate: toViewDate(
        timezone,
        issueModel.extendedData.finalStartDate
      ),
      executedByCompanies: !issueModel.extendedData.executedByCompanies
        ? []
        : issueModel.extendedData.executedByCompanies
            ?.map((company) => {
              const found = subcontractors[company];
              return found && { _id: found._id, label: found.shortLabel };
            })
            .filter((x) => x),
      completedAmount: issueModel.extendedData.completedAmount,
      targetAmount: issueModel.extendedData.targetAmount,
      amountPercentage: issueModel.extendedData.amountPercentage,
      completionDateDelay: issueModel.extendedData.completionDateDelay,
      startDateDelay: issueModel.extendedData.startDateDelay,
      responseToAuthorDelay: issueModel.extendedData.responseToAuthorDelay,
      numberOfEmployees: issueModel.extendedData.numberOfEmployees,
      priority: findValue(
        findField(issueForm, 'extendedFields', 'priority'),
        issueModel.extendedData.priority
      ),
      projectStage: findValue(
        findField(issueForm, 'extendedFields', 'projectStage'),
        issueModel.extendedData.projectStage
      ),
      targetResponseToAuthorDate: toViewDate(
        timezone,
        issueModel.extendedData.targetResponseToAuthorDate
      ),
      finalResponseToAuthorDate: toViewDate(
        timezone,
        issueModel.extendedData.finalResponseToAuthorDate
      ),
      sickLeaveStartDate: toViewDate(
        timezone,
        issueModel.extendedData.sickLeaveStartDate
      ),
      sickLeaveEndDate: toViewDate(
        timezone,
        issueModel.extendedData.sickLeaveEndDate
      ),
      supervisor: findValue(
        findField(issueForm, 'extendedFields', 'supervisor'),
        issueModel.extendedData.supervisor
      ),
      victim: issueModel.extendedData.victim,
      victimJobTitle: issueModel.extendedData.victimJobTitle,
      employeeNumber: issueModel.extendedData.employeeNumber,
      employmentType: findValue(
        findField(issueForm, 'extendedFields', 'employmentType'),
        issueModel.extendedData.employmentType
      ),
      immediateMeasures: issueModel.extendedData.immediateMeasures,
      victimActivity: issueModel.extendedData.victimActivity,
      injuryTypes: findValues(
        findField(issueForm, 'extendedFields', 'injuryTypes'),
        issueModel.extendedData.injuryTypes
      ),
      injuredBodyParts: findValues(
        findField(issueForm, 'extendedFields', 'injuredBodyParts'),
        issueModel.extendedData.injuredBodyParts
      ),
      applicationNumber: issueModel.extendedData.applicationNumber,
      productType: issueModel.extendedData.productType,
      producerDistributor: issueModel.extendedData.producerDistributor,
      typeModel: issueModel.extendedData.typeModel,
      installationLocation: issueModel.extendedData.installationLocation,
      requirements: findValues(
        findField(issueForm, 'extendedFields', 'requirements'),
        issueModel.extendedData.requirements
      ),
      installationBasis: findValues(
        findField(issueForm, 'extendedFields', 'installationBasis'),
        issueModel.extendedData.installationBasis
      ),
      constructionProductDocuments: findValues(
        findField(issueForm, 'extendedFields', 'constructionProductDocuments'),
        issueModel.extendedData.constructionProductDocuments
      ),
      deviceDocuments: findValues(
        findField(issueForm, 'extendedFields', 'deviceDocuments'),
        issueModel.extendedData.deviceDocuments
      ),
      additionalDocuments: findValues(
        findField(issueForm, 'extendedFields', 'additionalDocuments'),
        issueModel.extendedData.additionalDocuments
      ),
    },
    hashtag: issueModel.hashtag,
    inspection: issueModel.inspection,
    inspectionDisplayCode: issueModel.inspectionNumberSearch ?? '',
    primaryData: {
      site: toLabelledEntity(issueSite),
      title: issueModel.primaryData.title,
      assignee: userWithPersonalDataToOptionalLabelled(
        issueModel.primaryData.assignee,
        organizationId
      ),
      description: issueModel.primaryData.description,
      detectionDate: toViewDate(
        timezone,
        issueModel.primaryData.detectionDate
      ),
      subcontractors: !issueModel.primaryData.subcontractors
        ? []
        : issueModel.primaryData.subcontractors
            ?.map((subcontractor) => {
              const found = subcontractors[subcontractor];
              return found && { _id: found._id, label: found.shortLabel };
            })
            .filter((x) => x),
      contractNumbers: !issueModel.primaryData.contractNumbers
        ? []
        : (issueModel.primaryData.contractNumbers
            ?.map((contract) =>
              toUnsafeLabelledEntity(contractNumbers[contract])
            )
            .filter((x) => x) as LabelledEntity[]),
      level: toLabelledEntity(issueLevel),
      positionOnMap: issueModel.primaryData.positionOnMap,
      executor: userWithPersonalDataToOptionalLabelled(
        issueModel.primaryData.executor,
        organizationId
      ),
      finalAreas: issueModel.primaryData.finalAreas,
      selectedLocationType: issueModel.primaryData.selectedLocationType,
      targetAreas: issueModel.primaryData.targetAreas,
    },
    events: [],
    incomingEmailMessages: [],
    process: toLabelledEntity(issueProcess),
    protocolItem: issueModel.protocolItem,
    stageId: issueModel.stageId,
    workflowId: issueModel.workflowId,
    userAccesses: issueModel.userAccesses,
    workflowDecisions: issueModel.workflowDecisions ?? [],
    // Do NOT coerce to `null`: `undefined` ("not computed") must stay
    // distinguishable from `null` ("N/A"). See `IssueOnSingleView.lastDecisionStatus`.
    lastDecisionStatus: issueModel.lastDecisionStatus,
    latestDecisionId: issueModel.latestDecisionId,
    mainImage: issueModel.mainImage,
    emailAddress: issueModel.emailAddress,
    ncrNumber,
    linkedDocumentation: issueModel.linkedDocumentation ?? [],
  };
}

function findField(
  form: LegacyIssueForm,
  fieldType: 'extendedFields' | 'primaryFields',
  fieldName: string
): FieldSkeleton | undefined {
  return form[fieldType].find((field) => field.name === fieldName);
}

function findValue(
  fieldSkeleton: FieldSkeleton | undefined,
  valueToFind: string | undefined | null
): LabelledEntity | undefined {
  if (!fieldSkeleton || !valueToFind) return undefined;
  return (fieldSkeleton.items ?? []).find(
    (item) => item._id === valueToFind
  );
}

function findValues(
  fieldSkeleton: FieldSkeleton | undefined,
  valuesToFind: string[] | undefined | null
): LabelledEntity[] {
  if (!fieldSkeleton || !valuesToFind) return [];
  const map =
    (fieldSkeleton.items ?? []).reduce<HashMap<LabelledEntity>>(
      (result, item) => {
        result[item._id] = item;
        return result;
      },
      {} as HashMap<LabelledEntity>
    ) || {};

  return valuesToFind.map((value) => map[value]).filter((x) => x);
}
